// terminFormShared.ts — geteilte Bausteine der Termin-ANLAGE/-Bearbeitung
// (Termine-SP5). Ziel: das große `TerminFormWindow` (Edit + Alt-Create) und der
// schlanke `TerminLightDialog` (neuer Create-Flow) teilen EINE Quelle für die
// heiklen Stellen — dedupliziertes Teilnehmer-Handling, die TER-D6-
// Abwesenheits-Warnung und die Ganztags-+1-Tag-Payload-Bildung. KEIN Fork:
// beide Dialoge rufen exakt dieselben Helfer, sonst driften Grid, Warnung und
// Graph-Payload auseinander.
//
// Die reinen Funktionen sind React-frei (unter vitest testbar); nur der TER-D6-
// Hook (`useInviteWarnings`) bündelt den Effekt-Boilerplate, damit ihn beide
// Dialoge identisch nutzen können.

import { useEffect, useMemo, useState } from "react";
import {
    calendarService,
    type CreateEventInput,
    type InviteWarning,
    type RaumVerfuegbarkeit,
    type RecurrenceInput,
    type RoomDto,
    type ScheduleResult,
} from "../../services/calendarService";
import { userDisplay, type AppUser } from "../../services/userService";
import {
    graphMailService,
    type EmailRecipientHit,
    type MailboxAccess,
} from "../../services/graphMailService";
import { resolveDefaultCalendarMailbox } from "../../utils/mailboxAccess";
import { uhrzeitGueltig } from "../windowManager/terminFormLogik";
import type { ReminderPreset } from "./RecurrencePicker";
import {
    externeTeilnehmerAdressen,
    interneAdressSet,
    kandidatenAusTreffern,
    type KontaktKandidat,
} from "./terminVorgang";

/** Teilnehmer, wie der Aufrufer sie liefert (Name optional). */
export interface AttendeeInput {
    address: string;
    name?: string;
}

/** Normalisierter Teilnehmer (Name immer gesetzt — Fallback = Adresse). */
export interface Attendee {
    address: string;
    name: string;
}

export const pad = (n: number): string => String(n).padStart(2, "0");
export const ymd = (d: Date): string =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** "HH:mm" → Stunden/Minuten (ungültiger String ⇒ 00:00). */
export function parseZeit(z: string): { h: number; m: number } {
    const m = /^(\d{1,2}):(\d{2})$/.exec(z.trim());
    return m ? { h: Number(m[1]), m: Number(m[2]) } : { h: 0, m: 0 };
}

/** Label je Abwesenheits-Typ für die Einladungs-Warnung (TER-D6). */
export const ABWESENHEIT_LABEL: Record<string, string> = {
    urlaub: "im Urlaub",
    krank: "krank",
    homeoffice: "im Homeoffice",
    abwesend: "abwesend",
};

/** Erinnerung-Presets → Label (Reminder-Select in beiden Dialogen). */
export const REMINDER_OPTIONS: { value: ReminderPreset; label: string }[] = [
    { value: "none", label: "Keine" },
    { value: "15", label: "15 Minuten vorher" },
    { value: "30", label: "30 Minuten vorher" },
    { value: "60", label: "1 Stunde vorher" },
    { value: "1440", label: "1 Tag vorher" },
];

/** Reminder-Minuten → nächstgelegenes Preset (für die Vorbelegung beim Edit). */
export function minutesToPreset(on: boolean, min: number | null): ReminderPreset {
    if (!on || min == null) return "none";
    const presets: ReminderPreset[] = ["15", "30", "60", "1440"];
    if (presets.includes(String(min) as ReminderPreset)) return String(min) as ReminderPreset;
    // unbekannter Wert → auf den nächstkleineren bekannten Preset abbilden
    if (min >= 1440) return "1440";
    if (min >= 60) return "60";
    if (min >= 30) return "30";
    return "15";
}

/**
 * Teilnehmer-Liste deduplizieren: lowercased Adresse, ERSTE Nennung gewinnt,
 * leere Adressen raus, Name-Fallback = Adresse. Quelle für das Prefill
 * (Vg-SP6 `initialAttendees`) in BEIDEN Dialogen — ohne diese eine Funktion
 * würde der Austausch-Flow (#915) beim Umstieg auf den Light-Dialog seine
 * Teilnehmer-Vorbelegung verlieren.
 */
export function dedupeAttendees(list: AttendeeInput[] | null | undefined): Attendee[] {
    if (!list?.length) return [];
    const seen = new Set<string>();
    const out: Attendee[] = [];
    for (const a of list) {
        const address = a.address?.trim();
        if (!address) continue;
        const lc = address.toLowerCase();
        if (seen.has(lc)) continue;
        seen.add(lc);
        out.push({ address, name: a.name?.trim() || address });
    }
    return out;
}

/**
 * Einen Teilnehmer in eine bestehende Liste aufnehmen (Duplikat = No-Op,
 * case-insensitive). Leerer Input ⇒ Liste unverändert zurück.
 */
export function upsertAttendee(list: Attendee[], address: string, name?: string): Attendee[] {
    const clean = address.trim();
    if (!clean) return list;
    if (list.some((a) => a.address.toLowerCase() === clean.toLowerCase())) return list;
    return [...list, { address: clean, name: name?.trim() || clean }];
}

/**
 * Interne KMVS-Teilnehmer (für TER-D6): mappt attendee.address → AppUser.email.
 * Externe (Kunden) fallen raus — sie werden nicht auf Abwesenheit geprüft.
 */
export function mapInterneTeilnehmer(
    attendees: Attendee[],
    appUsers: AppUser[],
): { mailbox: string; name: string }[] {
    return attendees
        .map((a) => {
            const u = appUsers.find(
                (x) => (x.email ?? "").toLowerCase() === a.address.toLowerCase(),
            );
            return u?.email ? { mailbox: u.email, name: userDisplay(u) } : null;
        })
        .filter((x): x is { mailbox: string; name: string } => x !== null);
}

/**
 * Start/Ende als NAIVE-LOKALE ISO-Strings (Graph-Konvention, timeZone extern).
 * Ganztägig ⇒ Start 00:00 des Start-Tags, Ende 00:00 des Folgetags NACH dem
 * Ende-Tag (EXKLUSIV, Graph-+1-Tag). Diese eine Rechnung teilen Anlage, Update
 * UND die TER-D6-Warnung — sonst spannen Grid und Warnung unterschiedliche
 * Zeiträume.
 */
export function buildStartEnd(
    startDate: Date,
    endDate: Date,
    startTime: string,
    endTime: string,
    allDay: boolean,
): { start: string; end: string } {
    if (allDay) {
        const endNext = new Date(endDate);
        endNext.setDate(endNext.getDate() + 1);
        return { start: `${ymd(startDate)}T00:00:00`, end: `${ymd(endNext)}T00:00:00` };
    }
    return { start: `${ymd(startDate)}T${startTime}:00`, end: `${ymd(endDate)}T${endTime}:00` };
}

/** Teilnehmer + optionaler Raum → Graph-Attendee-Payload (Raum = resource). */
export function buildAttendeePayload(
    attendees: Attendee[],
    roomAddress: string,
    rooms: RoomDto[],
): NonNullable<CreateEventInput["attendees"]> {
    const payload: NonNullable<CreateEventInput["attendees"]> = attendees.map((a) => ({
        address: a.address,
        name: a.name,
        type: "required",
    }));
    if (roomAddress) {
        const room = rooms.find((r) => r.emailAddress === roomAddress);
        payload.push({
            address: roomAddress,
            name: room?.displayName || roomAddress,
            type: "resource",
        });
    }
    return payload;
}

/** Serie auf den Start ausrichten (range.startDate = Tag des Starts). */
export function buildRecurrencePayload(
    recurrence: RecurrenceInput | null,
    startDate: Date,
): RecurrenceInput | null {
    return recurrence
        ? { ...recurrence, range: { ...recurrence.range, startDate: ymd(startDate) } }
        : null;
}

/** Reminder-Preset → Graph-Reminder-Felder. */
export function reminderFromPreset(preset: ReminderPreset): {
    isReminderOn: boolean;
    reminderMinutesBeforeStart: number;
} {
    const on = preset !== "none";
    return { isReminderOn: on, reminderMinutesBeforeStart: on ? Number(preset) : 0 };
}

/**
 * Alle Bausteine der Termin-ANLAGE zu EINER `CreateEventInput` bündeln — denselben
 * calendarService-Create-Pfad nutzen große und schlanke Dialog-Variante.
 */
export function buildCreateEventInput(args: {
    mailbox: string;
    subject: string;
    body: string;
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
    allDay: boolean;
    isOnlineMeeting: boolean;
    location?: string;
    attendees: Attendee[];
    roomAddress: string;
    rooms: RoomDto[];
    recurrence: RecurrenceInput | null;
    reminderPreset: ReminderPreset;
    /** Privater Termin (PO 27.07.2026) — für Kollegen maskiert. */
    istPrivat?: boolean;
}): CreateEventInput {
    const { start, end } = buildStartEnd(
        args.startDate,
        args.endDate,
        args.startTime,
        args.endTime,
        args.allDay,
    );
    const attendeePayload = buildAttendeePayload(args.attendees, args.roomAddress, args.rooms);
    const reminder = reminderFromPreset(args.reminderPreset);
    return {
        mailbox: args.mailbox,
        subject: args.subject,
        start,
        end,
        timeZone: "Europe/Berlin",
        body: args.body,
        contentType: "HTML",
        isAllDay: args.allDay,
        isOnlineMeeting: args.isOnlineMeeting,
        location: args.location || undefined,
        attendees: attendeePayload.length > 0 ? attendeePayload : undefined,
        recurrence: buildRecurrencePayload(args.recurrence, args.startDate) ?? undefined,
        // Nur senden, wenn gesetzt — sonst überschriebe ein `false` beim Bearbeiten
        // eine Privat-Markierung, die in Outlook gesetzt wurde.
        ...(args.istPrivat ? { istPrivat: true } : {}),
        ...reminder,
    };
}

/** WRITE-Kalender-Postfächer (Absende-Postfach-Auswahl im Light-Dialog). */
export function writeableCalendarMailboxes(mailboxes: MailboxAccess[]): MailboxAccess[] {
    return mailboxes.filter((m) => m.calendarAccess === "WRITE");
}

/**
 * Ziel-Postfach der Anlage: ein explizit übergebenes Postfach gewinnt, sonst
 * das EIGENE WRITE-Kalender-Postfach (`resolveDefaultCalendarMailbox`), zuletzt
 * `currentMailbox`. Deckungsgleich mit der Ableitung im großen Fenster.
 *
 * ══ WAS `explicit` HEISST ═════════════════════════════════════════════════
 * Ein WUNSCH — „lege es bitte dort an" —, nicht „wo der Benutzer gerade
 * steht". Genau diese Verwechslung war bis zum 22.09.2026 der Fehler: Die
 * Kalender-Einstiege reichten `currentMailbox` als `explicit` durch, also das
 * im MAIL-Modul gewählte Postfach. Wer in cbb@ arbeitete — dort hängen die
 * Vorgangs-Mails — legte jeden neuen Termin still in cbb@ an, und der Fallback
 * auf das eigene Postfach kam nie zum Zuge, obwohl er richtig war.
 *
 * PO 22.09.2026: „bei neuen Termin immer die eigene Adresse verwenden und ganz
 * oben … kann man das noch ändern." Wer `explicit` setzt, braucht also einen
 * echten Grund; „das Modul nebenan zeigt gerade X" ist keiner.
 */
export function resolveCreateTargetMailbox(
    mailboxes: MailboxAccess[],
    ownMailbox: string | null,
    currentMailbox: string | null,
    explicit?: string | null,
): string {
    const fallback =
        resolveDefaultCalendarMailbox(mailboxes, ownMailbox)?.mailboxAddress ?? currentMailbox;
    return (explicit ?? fallback) ?? currentMailbox ?? "";
}

/** Darf im Ziel-Postfach ein Teams-Online-Meeting angelegt werden? */
export function canCreateTeams(mailboxes: MailboxAccess[], mailbox: string): boolean {
    const mb = mailboxes.find(
        (m) => m.mailboxAddress.toLowerCase() === (mailbox ?? "").toLowerCase(),
    );
    return !!mb?.canCreateTeamsMeetings;
}

/**
 * TER-D6 Einladungs-Warnung (nicht blockierend). Prüft je INTERNEM Teilnehmer,
 * ob im Termin-Zeitraum eine ganztägige Abwesenheit liegt. `aktiv` gatet auf
 * 'austausch' (Kundentermine warnen nicht). Gibt zusätzlich die aufgelöste
 * interne Teilnehmerliste zurück (für die Namens-Anzeige in der Warnung).
 */
export function useInviteWarnings(params: {
    aktiv: boolean;
    attendees: Attendee[];
    appUsers: AppUser[];
    startDate: Date | null;
    endDate: Date | null;
    startTime: string;
    endTime: string;
    allDay: boolean;
}): { warnings: InviteWarning[]; interneTeilnehmer: { mailbox: string; name: string }[] } {
    const { aktiv, attendees, appUsers, startDate, endDate, startTime, endTime, allDay } = params;

    const interneTeilnehmer = useMemo(
        () => mapInterneTeilnehmer(attendees, appUsers),
        [attendees, appUsers],
    );

    const [warnings, setWarnings] = useState<InviteWarning[]>([]);

    useEffect(() => {
        // T-267/T-269: halb getippte Uhrzeit ⇒ kein Abruf mit „…T:00" (400).
        const zeitUnvollstaendig = !allDay && (!uhrzeitGueltig(startTime) || !uhrzeitGueltig(endTime));
        if (!aktiv || interneTeilnehmer.length === 0 || !startDate || !endDate || zeitUnvollstaendig) {
            setWarnings([]);
            return;
        }
        const { start, end } = buildStartEnd(startDate, endDate, startTime, endTime, allDay);
        let cancelled = false;
        calendarService
            .getInviteWarnings(
                interneTeilnehmer.map((t) => t.mailbox),
                start,
                end,
            )
            .then((w) => {
                if (!cancelled) setWarnings(w);
            })
            .catch(() => {
                if (!cancelled) setWarnings([]);
            });
        return () => {
            cancelled = true;
        };
    }, [aktiv, interneTeilnehmer, startDate, endDate, startTime, endTime, allDay]);

    return { warnings, interneTeilnehmer };
}

// ── Raum-Verfügbarkeit (Termine-SP6, erstmalige FE-Nutzung von getSchedule) ──

/** Verfügbarkeitszustand eines Raums im gewählten Zeitfenster. */
export type RoomAvailability = "free" | "busy" | "unknown";

/**
 * Ein `getSchedule`-Ergebnis (free/busy) zu einem Verfügbarkeitszustand
 * verdichten. Der `availabilityView`-String kodiert je Intervall eine Ziffer
 * (0=frei, 1=vorläufig, 2=belegt, 3=abwesend, 4=woanders). Frei = ALLE Ziffern
 * '0'. Fehler oder leere Sicht ⇒ `unknown` (weder als frei noch als belegt
 * markieren — sonst würde ein Graph-Aussetzer alle Räume fälschlich ausgrauen).
 */
export function roomAvailabilityFromResult(result: ScheduleResult | undefined): RoomAvailability {
    if (!result || result.error) return "unknown";
    const view = result.availabilityView ?? "";
    if (view.length === 0) return "unknown";
    return view.split("").every((c) => c === "0") ? "free" : "busy";
}

/**
 * Raum-Verfügbarkeit im gewählten Zeitfenster (Termine-SP6). Ruft `getSchedule`
 * (free/busy) mit `organizerMailbox = Absende-Postfach` — beim Postfach-Wechsel
 * MITWECHSELN, da das Backend `assertCalendarRead` auf dieses Postfach fährt.
 * Debounced (Default 400 ms), damit ein Zeit-Scrubbing keinen Request-Sturm
 * auslöst. Intervall = Termindauer (5…1440 min), so verdichtet Graph das ganze
 * Fenster zu EINER Ziffer je Raum. Ergebnis: Map (lowercased Raum-Adresse →
 * Zustand). Best-effort: bei Fehler leere Map (alle Räume `unknown`).
 */
export function useRoomAvailability(params: {
    organizerMailbox: string | null;
    roomAddresses: string[];
    start: Date | null;
    end: Date | null;
    active: boolean;
    debounceMs?: number;
}): { statusByRoom: Record<string, RoomAvailability>; loading: boolean } {
    const { organizerMailbox, roomAddresses, start, end, active, debounceMs = 400 } = params;
    const [statusByRoom, setStatusByRoom] = useState<Record<string, RoomAvailability>>({});
    const [loading, setLoading] = useState(false);

    // Stabile Effekt-Keys: Adress-Set (sortiert) + Zeit-Millis statt Objekt-Identität.
    const addrKey = useMemo(
        () => roomAddresses.map((a) => a.toLowerCase()).sort().join(","),
        [roomAddresses],
    );
    const startMs = start ? start.getTime() : 0;
    const endMs = end ? end.getTime() : 0;

    useEffect(() => {
        if (!active || !organizerMailbox || addrKey.length === 0 || endMs <= startMs) {
            setStatusByRoom({});
            setLoading(false);
            return;
        }
        const addresses = addrKey.split(",");
        let cancelled = false;
        setLoading(true);
        const durationMin = Math.round((endMs - startMs) / 60000);
        const interval = Math.min(1440, Math.max(5, durationMin || 30));
        const timer = setTimeout(() => {
            calendarService
                .getSchedule(
                    organizerMailbox,
                    addresses,
                    new Date(startMs).toISOString(),
                    new Date(endMs).toISOString(),
                    interval,
                )
                .then((results) => {
                    if (cancelled) return;
                    const byId = new Map(results.map((r) => [r.scheduleId.toLowerCase(), r]));
                    const next: Record<string, RoomAvailability> = {};
                    for (const addr of addresses) {
                        next[addr] = roomAvailabilityFromResult(byId.get(addr));
                    }
                    setStatusByRoom(next);
                })
                .catch((err) => {
                    if (!cancelled) {
                        console.warn("[useRoomAvailability] getSchedule fehlgeschlagen", err);
                        setStatusByRoom({});
                    }
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });
        }, debounceMs);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [active, organizerMailbox, addrKey, startMs, endMs, debounceMs]);

    return { statusByRoom, loading };
}

/**
 * Raum-Verfügbarkeit für die Raumwahl im Termin-Formular (GET
 * calendar/rooms/verfuegbarkeit). Zeitraum wie beim Anlegen (`buildStartEnd`,
 * Ganztägig = Mitternacht bis Mitternacht des Folgetags in der Ortszeit des
 * Browsers). Entprellt (Default 400 ms), damit ein Durchscrollen der Uhrzeit
 * keinen Abfragesturm auslöst. Beim Bearbeiten `eigenerTermin` angeben — dann
 * zählt der Termin selbst nicht als Belegung.
 *
 * `fehler` = Verfügbarkeit gerade nicht prüfbar (Netz-/Graph-Fehler); die
 * Raumliste bleibt dann ohne Kennzeichnung wählbar.
 */
export function useRaumVerfuegbarkeit(params: {
    aktiv: boolean;
    startDate: Date | null;
    endDate: Date | null;
    startTime: string;
    endTime: string;
    allDay: boolean;
    eigenerTermin?: { mailbox: string; eventId: string } | null;
    debounceMs?: number;
}): { nachRaum: Record<string, RaumVerfuegbarkeit>; laedt: boolean; fehler: boolean } {
    const { aktiv, startDate, endDate, startTime, endTime, allDay, eigenerTermin, debounceMs = 400 } =
        params;
    const [nachRaum, setNachRaum] = useState<Record<string, RaumVerfuegbarkeit>>({});
    const [laedt, setLaedt] = useState(false);
    const [fehler, setFehler] = useState(false);

    const fenster = useMemo(() => {
        if (!startDate || !endDate) return null;
        const { start, end } = buildStartEnd(startDate, endDate, startTime, endTime, allDay);
        // Ohne Zonen-Suffix parst JS die Zeit als Ortszeit des Browsers.
        const s = new Date(start).getTime();
        const e = new Date(end).getTime();
        return Number.isNaN(s) || Number.isNaN(e) || e <= s ? null : { s, e };
    }, [startDate, endDate, startTime, endTime, allDay]);
    const vonMs = fenster?.s ?? 0;
    const bisMs = fenster?.e ?? 0;
    const eigenMb = eigenerTermin?.mailbox ?? "";
    const eigenId = eigenerTermin?.eventId ?? "";

    useEffect(() => {
        if (!aktiv || !vonMs || !bisMs) {
            setNachRaum({});
            setLaedt(false);
            setFehler(false);
            return;
        }
        let abgebrochen = false;
        setLaedt(true);
        const timer = setTimeout(() => {
            calendarService
                .roomAvailability(
                    new Date(vonMs),
                    new Date(bisMs),
                    eigenMb && eigenId ? { mailbox: eigenMb, eventId: eigenId } : null,
                )
                .then((antwort) => {
                    if (abgebrochen) return;
                    const map: Record<string, RaumVerfuegbarkeit> = {};
                    for (const v of antwort.raeume) map[v.emailAddress.toLowerCase()] = v;
                    setNachRaum(map);
                    setFehler(!antwort.geprueft);
                })
                .catch(() => {
                    if (abgebrochen) return;
                    setNachRaum({});
                    setFehler(true);
                })
                .finally(() => {
                    if (!abgebrochen) setLaedt(false);
                });
        }, debounceMs);
        return () => {
            abgebrochen = true;
            clearTimeout(timer);
        };
    }, [aktiv, vonMs, bisMs, eigenMb, eigenId, debounceMs]);

    return { nachRaum, laedt, fehler };
}

/**
 * Kunde-Vorschlag aus EXTERNEN Teilnehmern (Termine-SP6). Wiederverwendung der
 * reinen Ableitung aus `terminVorgang.ts`: interne Kollegen-Adressen (App-User +
 * eigenes Postfach) fallen raus, die verbleibenden externen Adressen werden über
 * die Empfänger-Kontaktsuche exakt aufgelöst und je contactId dedupliziert.
 * Debounced (Default 400 ms). `aktiv` gatet die Auflösung (z. B. nur solange
 * KEIN Kunde manuell gewählt ist). Best-effort: bei Fehler leere Liste.
 */
export function useKundeVorschlag(params: {
    aktiv: boolean;
    attendees: Attendee[];
    appUsers: AppUser[];
    eigeneMailbox: string | null;
    debounceMs?: number;
    max?: number;
}): { vorschlaege: KontaktKandidat[]; loading: boolean } {
    const { aktiv, attendees, appUsers, eigeneMailbox, debounceMs = 400, max = 3 } = params;
    const [vorschlaege, setVorschlaege] = useState<KontaktKandidat[]>([]);
    const [loading, setLoading] = useState(false);

    const intern = useMemo(
        () => interneAdressSet(appUsers, eigeneMailbox),
        [appUsers, eigeneMailbox],
    );
    const adressen = useMemo(
        () => externeTeilnehmerAdressen({ attendees }, intern),
        [attendees, intern],
    );
    const adrKey = adressen.join(",");

    useEffect(() => {
        if (!aktiv || adressen.length === 0) {
            setVorschlaege([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const timer = setTimeout(() => {
            Promise.all(
                adressen.map(async (email) => ({
                    email,
                    hits: await graphMailService
                        .searchEmailRecipients(email)
                        .catch(() => [] as EmailRecipientHit[]),
                })),
            )
                .then((paare) => {
                    if (cancelled) return;
                    const { kandidaten } = kandidatenAusTreffern(paare);
                    setVorschlaege(kandidaten.slice(0, max));
                })
                .catch(() => {
                    if (!cancelled) setVorschlaege([]);
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });
        }, debounceMs);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aktiv, adrKey, debounceMs, max]);

    return { vorschlaege, loading };
}
